import { useState, useEffect, useRef } from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import { PageHeader } from '../../components/common/PageHeader';
import { useSnackbar } from '../../components/common/SnackbarProvider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { PersonalKnowledgeSection } from '../../components/profile/PersonalKnowledgeSection';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetCurrentUserProfile,
  useUpdateCurrentUserProfile,
  useUploadAvatar,
  useRemoveAvatar,
  getGetCurrentUserProfileQueryKey,
} from '../../api/generated/generated';
import type { UserProfile } from '../../api/generated';
import { ConnectedAccountsSection } from '../../components/profile/ConnectedAccountsSection';

export function SettingsPage() {
  const queryClient = useQueryClient();
  const { data: profileRes, isLoading, error } = useGetCurrentUserProfile();
  const profile = profileRes?.data as UserProfile | undefined;
  const updateProfile = useUpdateCurrentUserProfile({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetCurrentUserProfileQueryKey() });
      },
    },
  });
  const uploadAvatar = useUploadAvatar({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetCurrentUserProfileQueryKey() });
      },
    },
  });
  const removeAvatar = useRemoveAvatar({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetCurrentUserProfileQueryKey() });
      },
    },
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [writingStyle, setWritingStyle] = useState('');
  const [styleEnabled, setStyleEnabled] = useState(false);
  const { showSnackbar } = useSnackbar();

  // Seed form state once per loaded profile; avoid clobbering in-flight edits
  // when the query refetches after a successful save.
  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name);
      setLastName(profile.last_name);
      setJobTitle(profile.job_title || '');
      setWritingStyle(profile.writing_style || '');
      setStyleEnabled(profile.writing_style_enabled ?? false);
    }
  }, [profile?.id]);

  const handleSaveCoaching = async () => {
    try {
      await updateProfile.mutateAsync({
        data: { writing_style: writingStyle, writing_style_enabled: styleEnabled },
      });
      showSnackbar('Coaching profile updated.');
    } catch {
      showSnackbar('Failed to update coaching profile.', 'error');
    }
  };

  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync({
        data: {
          first_name: firstName,
          last_name: lastName,
          job_title: jobTitle || undefined,
        },
      });
      showSnackbar('Profile updated successfully.');
    } catch {
      showSnackbar('Failed to update profile.', 'error');
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadAvatar.mutateAsync({ data: { file } });
      showSnackbar('Avatar uploaded successfully.');
    } catch {
      showSnackbar('Failed to upload avatar.', 'error');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAvatarRemove = async () => {
    try {
      await removeAvatar.mutateAsync();
      showSnackbar('Avatar removed.');
    } catch {
      showSnackbar('Failed to remove avatar.', 'error');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load profile.
      </Alert>
    );
  }

  const initials =
    `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase() || '?';

  return (
    <Box>
      <PageHeader title="Settings" />
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Avatar
        </Typography>
        <Stack direction="row" spacing={2} sx={{
          alignItems: "center"
        }}>
          <Avatar
            src={profile.avatar_url || undefined}
            sx={{ width: 80, height: 80, fontSize: '2rem' }}
          >
            {initials}
          </Avatar>
          <Box>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              ref={fileInputRef}
              onChange={handleAvatarUpload}
            />
            <Button
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadAvatar.isPending}
              size="small"
            >
              {uploadAvatar.isPending ? 'Uploading...' : 'Upload'}
            </Button>
            {profile.avatar_url && (
              <IconButton
                onClick={handleAvatarRemove}
                disabled={removeAvatar.isPending}
                size="small"
                sx={{ ml: 1 }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        </Stack>
      </Paper>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Profile
        </Typography>

        <TextField
          label="Email"
          value={profile.email}
          fullWidth
          disabled
          sx={{ mb: 2 }}
        />
        <TextField
          label="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="Job Title"
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 2
          }}>
          Account created: {new Date(profile.created_at).toLocaleDateString()}
        </Typography>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={updateProfile.isPending}
        >
          {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </Paper>
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          My Coaching Profile
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 2
          }}>
          Paste a sample of how you write (or describe your style). When Style mode is on, emails and
          messages the AI drafts for you are written in your voice. It does not change your coaching
          feedback.
        </Typography>
        <TextField
          label="Writing style sample"
          value={writingStyle}
          onChange={(e) => setWritingStyle(e.target.value)}
          fullWidth
          multiline
          minRows={5}
          sx={{ mb: 1 }}
          slotProps={{
            htmlInput: { maxLength: 4000 }
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={styleEnabled}
              onChange={(e) => setStyleEnabled(e.target.checked)}
            />
          }
          label="Style mode — use my writing style for drafts"
          sx={{ mb: 2, display: 'block' }}
        />
        <Button
          variant="contained"
          onClick={handleSaveCoaching}
          disabled={updateProfile.isPending}
        >
          {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </Paper>
      <PersonalKnowledgeSection />
      <ConnectedAccountsSection />
    </Box>
  );
}
